/**
 * MOCK · ASSUMED (CT-5527) — the Activity feed behind a profile's Activity tab.
 *
 * Shapes follow the contract in CT-5527 and are marked `TODO(CT-5527 / backend)`
 * at the call site: real snake_case, real envelope, only the transport stubbed.
 *
 * Dates are RELATIVE to the moment the seed is built, so the "Today" / "A month
 * ago" headings stay meaningful whenever the app runs instead of rotting into a
 * list of absolute dates nobody can read.
 *
 * Coverage:
 *   every customer        28 emails and SMS in both directions — several pages,
 *                         a long AI summary (expandable), rows with none
 *   customers/c-2         no activity at all — the empty state
 *   customers/c-3         the request fails — the error + retry state
 *
 * Calls and meetings are NOT seeded: v2.0 leaves them out of Communications.
 *
 * Notes (CT-5528) are NOT rows of this seed: they live in `notes.mock.js`, where
 * they can be created and edited, and are merged into the feed at query time.
 */

import { listMockHistoryRows } from './history.mock.js';
import { listMockNoteRows } from './notes.mock.js';

/** Entities whose feed is empty or fails, so those screens are reachable. */
export const ACTIVITY_EMPTY_ENTITY_IDS = Object.freeze(['c-2']);
export const ACTIVITY_FAILING_ENTITY_IDS = Object.freeze(['c-3']);

// Calls and meetings — the seeded call record and the ones logged by hand on
// History (CT-5724) — are communications too.
const COMMUNICATION_KINDS = ['email', 'sms', 'call', 'meeting'];

const QUICK_FILTER_KINDS = Object.freeze({
  all: [...COMMUNICATION_KINDS, 'note', 'task'],
  communications: COMMUNICATION_KINDS,
  notes: ['note'],
  tasks: ['task'],
});

const DAY_MS = 24 * 60 * 60 * 1000;

/** The windows the Period filter offers, in days. Its ids are what the request carries. */
const PERIOD_DAYS = Object.freeze({ 'last-7-days': 7, 'last-30-days': 30, 'last-90-days': 90 });

const STATUS_VARIANT = Object.freeze({
  Sent: 'informative',
  Delivered: 'success',
  Opened: 'success',
  Replied: 'success',
  Failed: 'error',
});

const daysAgoAt = (days, hour, minute) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

/** Who the account team writes to and hears from; Priya Nair is the account owner. */
const PEOPLE = ['Sandra Torres', 'Sarah Chen', 'David Park', 'Amy Chen'];

const EMAIL_SUBJECTS = [
  'Governance addendum for review',
  'Renewal quote for FY27',
  'Follow-up on the migration timeline',
  'Quarterly business review — agenda',
  'Escalation: platform outage',
  'Introduction to our executive sponsor',
  'Security questionnaire attached',
  'Re: pricing for the extra seats',
];

const SMS_BODIES = [
  'Can you confirm Thursday at 10?',
  'Sent you the signed order form.',
  'Running five minutes late to the call.',
  'Thanks — the numbers look right on our side.',
];

const LONG_SUMMARY =
  'Sandra has raised the unanswered migration timeline on the last two calls without a written answer, ' +
  'and she owns the budget line on a renewal that closes in 19 days. The governance addendum is still ' +
  'outstanding on our side and she is the only live contact on the account since June, so a written reply ' +
  'this week is the single action that moves the renewal.';

/** Days back from now for each of the 28 rows: a spread across every heading. */
const DAYS_BACK = [
  0, 0, 1, 3, 5, 9, 14, 21, 34, 38, 41, 50, 62, 66, 70, 95, 100, 110, 130, 160, 200, 250, 330, 380, 400, 420, 450, 500,
];

const buildRow = (daysBack, index) => {
  const kind = index % 3 === 2 ? 'sms' : 'email';
  const direction = index % 2 === 0 ? 'outbound' : 'inbound';
  const person = PEOPLE[index % PEOPLE.length];
  const statusLabel =
    kind === 'sms'
      ? ['Delivered', 'Failed', 'Sent'][index % 3]
      : ['Opened', 'Replied', 'Sent'][index % 3];

  return {
    id: `act-${index + 1}`,
    kind,
    direction,
    title:
      kind === 'email'
        ? EMAIL_SUBJECTS[index % EMAIL_SUBJECTS.length]
        : `SMS · ${SMS_BODIES[index % SMS_BODIES.length].slice(0, 24)}`,
    // Earlier in the day as the index grows, so the newest row is always the first seed row.
    occurred_at: daysAgoAt(daysBack, 8 - (index % 8), (index * 7) % 60),
    status: { label: statusLabel, variant: STATUS_VARIANT[statusLabel] },
    preview:
      kind === 'email'
        ? `Hi — ${EMAIL_SUBJECTS[index % EMAIL_SUBJECTS.length].toLowerCase()}. Let me know if anything is unclear.`
        : SMS_BODIES[index % SMS_BODIES.length],
    // The AI summary can be empty, and a long one has to expand.
    ai_summary: index === 0 ? LONG_SUMMARY : index % 4 === 3 ? null : `Short summary of message ${index + 1}.`,
    sender: direction === 'outbound' ? { name: 'Priya Nair' } : { name: person },
    recipients: direction === 'outbound' ? [{ name: person }] : [{ name: 'Priya Nair' }],
    channel: kind,
    status_id: statusLabel.toLowerCase(),
  };
};

const buildSeed = () => ({
  'customers/activity': { rows: DAYS_BACK.map(buildRow) },
});

let mockActivity = buildSeed();

export const getMockActivity = () => mockActivity;

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockActivityForTests = () => {
  mockActivity = buildSeed();
};

/**
 * The filter definitions each chip offers. They DIFFER per chip — Notes will
 * filter by scope, not by channel — which is why the frontend re-reads them from
 * the response whenever the chip changes and keeps no option list of its own.
 */
const FILTER_DEFS = Object.freeze({
  communications: [
    {
      id: 'channel',
      label: 'Channel',
      options: [
        { value: 'email', label: 'Email' },
        { value: 'sms', label: 'SMS' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: ['Sent', 'Delivered', 'Opened', 'Replied', 'Failed'].map((label) => ({
        value: label.toLowerCase(),
        label,
      })),
    },
    {
      id: 'period',
      label: 'Period',
      options: [
        { value: 'last-7-days', label: 'Last 7 days' },
        { value: 'last-30-days', label: 'Last 30 days' },
        { value: 'last-90-days', label: 'Last 90 days' },
      ],
    },
  ],
  // Notes have ONE metadatum worth narrowing by: who may read them (CT-5528).
  notes: [
    {
      id: 'scope',
      label: 'Scope',
      options: [
        { value: 'public', label: 'Public' },
        { value: 'private', label: 'Private' },
      ],
    },
  ],
});

export const activityFilterDefsFor = (quickFilter) => {
  if (quickFilter === 'notes') return FILTER_DEFS.notes;
  // Tasks (CT-5724) have no channel, status or period worth narrowing by yet.
  if (quickFilter === 'tasks') return [];
  return FILTER_DEFS.communications;
};

const matchesPeriod = (row, periodIds) => {
  const age = Date.now() - new Date(row.occurred_at).getTime();
  // Several periods are OR, like any other filter: the widest one wins.
  return periodIds.some((id) => age <= (PERIOD_DAYS[id] ?? 0) * DAY_MS);
};

const FILTER_MATCHERS = Object.freeze({
  channel: (row, values) => values.includes(row.channel),
  status: (row, values) => values.includes(row.status_id),
  period: matchesPeriod,
  scope: (row, values) => values.includes(row.scope),
});

/**
 * Narrow, search and order one entity's feed.
 *
 * Several values of ONE filter are OR; two different filters are AND — the same
 * meaning the generic tab list gives its chips. An unknown filter id narrows
 * nothing rather than throwing, so a payload that grows a filter does not break
 * an older mock.
 */
export const queryMockActivity = ({
  entityType,
  entityId,
  tabKey,
  quickFilter = 'all',
  filters = {},
  search = {},
  orderDirection = 'desc',
}) => {
  if (ACTIVITY_EMPTY_ENTITY_IDS.includes(entityId)) return [];

  const kinds = QUICK_FILTER_KINDS[quickFilter] ?? [];
  const needle = (search.query ?? '').trim().toLowerCase();

  const rows = [
    ...(getMockActivity()[tabKey]?.rows ?? []),
    ...listMockNoteRows(entityType, entityId),
    ...listMockHistoryRows(entityType, entityId),
  ];

  const matched = rows
    .filter((row) => kinds.includes(row.kind))
    .filter(
      (row) =>
        !needle ||
        `${row.title} ${row.ai_summary ?? ''} ${row.preview ?? ''}`.toLowerCase().includes(needle),
    )
    .filter((row) =>
      Object.entries(filters).every(([filterId, values]) => {
        if (!values?.length) return true;
        return FILTER_MATCHERS[filterId]?.(row, values) ?? true;
      }),
    )
    .sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));

  return orderDirection === 'asc' ? matched.reverse() : matched;
};

/** What the transport strips before a row leaves the mock: matcher-only fields. */
export const toApiActivityRow = ({ channel: _channel, status_id: _statusId, ...row }) => row;

export const hasMockActivity = (tabKey) => Boolean(getMockActivity()[tabKey]);
